/**
 * The dashboard's persisted display preferences — scoped to the signed-in user.
 *
 * Thin binding over the shared `DisplayPrefsStore` (`@authe/fe-commons/display`),
 * which owns the vocabulary + storage keys so a choice like the Overview activity
 * view (table vs graph) or live mode persists across reloads and never diverges
 * from how other surfaces name it.
 *
 * The store is a module singleton because every surface imports it directly, but
 * the KEYSPACE underneath it is not fixed: it starts app-wide (nobody is signed
 * in yet) and is re-pointed at the caller's identity once the session resolves —
 * see {@link setDisplayPrefsIdentity}, called by `<DisplayPrefsIdentity/>` in
 * `providers`. Without that, every preference here was written at the
 * APPLICATION level, which is one keyspace shared by every user and every tenant
 * signing in on that browser: on a shared workstation the second analyst
 * inherited the first one's graph tier, hidden panels, live mode and chip
 * settings. Two of the consuming hooks documented themselves as "remembered per
 * user" while doing this.
 *
 * Reads stay hierarchical (user → tenant → org → customer → app → global), so an
 * app-level value written before this fix is still found as a default; writes now
 * land under the user. Nothing needs migrating.
 */
import { createDisplayPrefsStore, type DisplayPrefsStore } from "@authe/fe-commons/display";
import {
  createScopedStorage,
  type ScopedStorage,
  type StorageIdentity,
} from "@authe/fe-commons/storage";
import { APPLICATION_SLUG } from "@/config";

/** The identity the keyspace is currently pointed at. */
let identity: StorageIdentity = { app: APPLICATION_SLUG };
let scope: ScopedStorage = createScopedStorage(identity);

/**
 * A stable façade over a swappable {@link ScopedStorage}.
 *
 * The prefs store captures its storage once, and surfaces capture the prefs
 * store once, so the object handed to `createDisplayPrefsStore` has to outlive
 * every identity change — it delegates instead of being replaced.
 */
type Level = Parameters<ScopedStorage["set"]>[2];

const storage = {
  get: <T,>(name: string, fallback: T): T => scope.get<T>(name, fallback),
  set: <T,>(name: string, value: T, level?: Level) => scope.set<T>(name, value, level),
  remove: (name: string, level?: Level) => scope.remove(name, level),
  /**
   * A derived store for another identity — a SNAPSHOT of the current scope, as
   * `DisplayPrefsStore.with` intends (it builds a second store rather than
   * re-pointing this one).
   */
  with: (patch: Parameters<ScopedStorage["with"]>[0]): ScopedStorage => scope.with(patch),
} as unknown as ScopedStorage;

export const displayPrefs: DisplayPrefsStore = createDisplayPrefsStore(storage);

/**
 * The same per-user keyspace, for feature state that is a CONVENIENCE — never
 * shared with a teammate and never authoritative (a case suggestion this reader
 * dismissed, say). Re-pointed on identity change with everything else here, so
 * one analyst's choices do not follow the browser to the next.
 */
export const userPrefsStorage: ScopedStorage = storage;

/** True when two identities name the same subject (avoids pointless re-points). */
function sameIdentity(a: StorageIdentity, b: StorageIdentity): boolean {
  return (
    a.app === b.app &&
    (a.customerId ?? null) === (b.customerId ?? null) &&
    (a.orgId ?? null) === (b.orgId ?? null) &&
    (a.tenantId ?? null) === (b.tenantId ?? null) &&
    (a.userId ?? null) === (b.userId ?? null)
  );
}

/**
 * Point the preference keyspace at `next` (the live session's identity).
 *
 * Idempotent, and safe to call on every render of the component that watches the
 * session. On sign-out the caller passes the app-only identity back, so the next
 * person to sign in on this browser starts from the shared defaults rather than
 * from the previous analyst's view.
 */
export function setDisplayPrefsIdentity(next: StorageIdentity): void {
  if (sameIdentity(identity, next)) return;
  identity = { ...next };
  scope = createScopedStorage(identity);
}

/** The identity preferences are currently written under (for tests + the debug panel). */
export function displayPrefsIdentity(): StorageIdentity {
  return { ...identity };
}
